import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Gauge, ArrowLeft, Search } from "lucide-react";

const fmtD = (iso) => (iso ? iso.split("-").reverse().join(".") : "—");

const levelCls = {
  high: "bg-red-50 text-red-700",
  low: "bg-green-50 text-green-700",
  normal: "",
};

const CarConsumptionCard = ({ car }) => {
  const highs = car.entries.filter((e) => e.level === "high").length;
  const lows = car.entries.filter((e) => e.level === "low").length;
  return (
    <div className="card-flat p-4" data-testid={`consum-car-${car.car_id}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
        <h2 className="font-display text-base font-bold">🚐 {car.car_label}</h2>
        <div className="flex items-center gap-2 text-xs">
          <span className="px-2 py-0.5 rounded bg-zinc-100 font-semibold tabular-nums">Media: {car.avg_l100} L/100km</span>
          <span className="px-2 py-0.5 rounded bg-zinc-100 font-semibold tabular-nums">Mediana: {car.median_l100}</span>
          {highs > 0 && <span className="px-2 py-0.5 rounded bg-red-100 text-red-700 font-bold">{highs} 🔺 mari</span>}
          {lows > 0 && <span className="px-2 py-0.5 rounded bg-green-100 text-green-700 font-bold">{lows} 🔻 mici</span>}
        </div>
      </div>
      <div className="overflow-x-auto border border-zinc-200 rounded">
        <table className="w-full text-xs">
          <thead className="bg-zinc-100 text-zinc-600">
            <tr>
              <th className="text-left px-2 py-1.5 font-semibold">Data</th>
              <th className="text-left px-2 py-1.5 font-semibold">Persoana</th>
              <th className="text-right px-2 py-1.5 font-semibold">Litri</th>
              <th className="text-right px-2 py-1.5 font-semibold">Km parcurși</th>
              <th className="text-right px-2 py-1.5 font-semibold">Odometru</th>
              <th className="text-right px-2 py-1.5 font-semibold">L/100km</th>
              <th className="text-right px-2 py-1.5 font-semibold">Abatere</th>
            </tr>
          </thead>
          <tbody>
            {car.entries.map((e, i) => (
              <tr key={i} className={`border-t border-zinc-100 ${levelCls[e.level]}`} data-testid={`consum-row-${car.car_id}-${i}`}>
                <td className="px-2 py-1.5 whitespace-nowrap">{fmtD(e.date)}</td>
                <td className="px-2 py-1.5 whitespace-nowrap">{e.persoana || "—"}</td>
                <td className="px-2 py-1.5 text-right tabular-nums">{e.liters} L</td>
                <td className="px-2 py-1.5 text-right tabular-nums">{e.km} km</td>
                <td className="px-2 py-1.5 text-right tabular-nums text-zinc-500">{e.odometer}</td>
                <td className={`px-2 py-1.5 text-right tabular-nums font-bold ${e.level === "high" ? "text-red-600" : e.level === "low" ? "text-green-600" : ""}`}>
                  {e.l100}
                </td>
                <td className={`px-2 py-1.5 text-right tabular-nums font-semibold ${e.level === "high" ? "text-red-600" : e.level === "low" ? "text-green-600" : "text-zinc-400"}`}>
                  {e.level === "high" && "🔺"}{e.level === "low" && "🔻"}{e.deviation_pct > 0 ? "+" : ""}{e.deviation_pct}%
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default function ConsumuriMasini() {
  const navigate = useNavigate();
  const [cars, setCars] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/cars/fuel-consumption-details").then((r) => setCars(r.data || [])).catch(() => setCars([]));
  }, []);

  const filtered = (cars || []).filter((c) => c.car_label.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-5" data-testid="page-consumuri-masini">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#FEF3C7] flex items-center justify-center text-[#D97706]">
            <Gauge />
          </div>
          <div>
            <h1 className="font-display text-2xl font-bold">Consumuri mașini</h1>
            <p className="text-xs text-zinc-500 mt-0.5">
              Toate consumurile calculate între alimentări · <span className="text-red-600 font-semibold">roșu = consum prea mare (≥ +15%)</span> · <span className="text-green-600 font-semibold">verde = consum prea mic (≤ −15%)</span> față de mediana mașinii.
            </p>
          </div>
        </div>
        <Button variant="outline" onClick={() => navigate("/combustibil")} data-testid="back-to-combustibil-btn">
          <ArrowLeft size={14} className="mr-1.5" /> Înapoi la Combustibil
        </Button>
      </div>

      <div className="relative max-w-xs">
        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Caută mașină..." className="pl-8" data-testid="consum-search-input" />
      </div>

      {cars === null && <div className="text-sm text-zinc-500">Se încarcă...</div>}
      {cars !== null && filtered.length === 0 && <div className="text-sm text-zinc-500 card-flat p-6 text-center">Nicio mașină cu alimentări consecutive înregistrate.</div>}
      {filtered.map((c) => (
        <CarConsumptionCard key={c.car_id} car={c} />
      ))}
    </div>
  );
}
